/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useMemo } from 'react';

import { Box } from '@adsk/alloy-react-box';
import Divider from '@adsk/alloy-react-divider';
import Dropdown from '@adsk/alloy-react-dropdown';
import FormField from '@adsk/alloy-react-form-field';
import { useLocale } from '@adsk/alloy-react-locale';
import Panel from '@adsk/alloy-react-panel';

import ActivityCodeFilters from './ActivityCodeFilters';
import CheckboxFilter from './CheckboxFilter';
import DateRangeFilter from './DateRangeFilter';
import QuickLookFilter from './QuickLookFilter';
import {
  ActivityStatus,
  DropdownMultiSelectProps,
  ScheduleFilters,
} from '../../types';
import { useScheduleContext } from '../ScheduleProvider';

function FiltersPanel({
  onClose,
  open,
  filters,
  setFilters,
}: {
  open: boolean;
  onClose: () => void;
  filters: ScheduleFilters;
  setFilters: (filters: ScheduleFilters) => void;
}) {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const onChangeFilter = useCallback(
    (
      key: string,
      value:
        | string
        | boolean
        | (Date | string | null | undefined)[]
        | undefined,
    ) => {
      const newFilters = { ...filters };
      if (!value || (Array.isArray(value) && value.length === 0)) {
        // Need to delete the entire key from the object because otherwise
        // ReferencePicker will understand it as if the filter exists
        // (even if the value is undefined or null)
        delete (newFilters as any)[key];
      } else {
        (newFilters as any)[key] = value;
      }
      setFilters(newFilters);
    },
    [setFilters, filters],
  );

  const statusOptions = useMemo(() => {
    return Object.keys(ActivityStatus).map((status) => ({
      value: status,
      label: T.translate(`FILTERS.${status}`),
    }));
  }, [T]);

  const statusValues = useMemo(() => {
    const filterStatus = filters?.statuses || [];
    return statusOptions.filter((item) =>
      filterStatus?.includes(item?.value as ActivityStatus),
    );
  }, [filters?.statuses, statusOptions]);

  const { activityCodesMap, laborResources = [] } = useScheduleContext();

  const resourceValues = useMemo(() => {
    return filters.resources
      ? laborResources.filter((resource) =>
          filters.resources?.includes(resource.value),
        )
      : [];
  }, [filters.resources, laborResources]);

  const handleSelectItemsChange = useCallback(
    (filterKey: string, { selectedItems = [] }: DropdownMultiSelectProps) => {
      const value = selectedItems?.map((item) => item?.value?.toString()) ?? [];
      onChangeFilter(filterKey, value);
    },
    [onChangeFilter],
  );

  const handleResourceSelectItemsChange = (
    changes: DropdownMultiSelectProps,
  ) => {
    handleSelectItemsChange('resources', changes);
  };

  const handleStatusSelectItemsChange = (changes: DropdownMultiSelectProps) => {
    handleSelectItemsChange('statuses', changes);
  };

  return (
    <Panel open={open} onClose={onClose} title={T.translate('FILTERS.TITLE')}>
      <Panel.Body>
        <Box
          display="grid"
          gridAutoFlow="column"
          justifyContent="flex-start"
          padding="8px 0 24px"
          style={{ columnGap: '24px' }}
        >
          <CheckboxFilter
            name="milestone"
            checked={!!filters.milestone}
            onChange={onChangeFilter}
            title={T.translate('FILTERS.MILESTONE')}
          />
          <CheckboxFilter
            name="critical"
            checked={!!filters.critical}
            onChange={onChangeFilter}
            title={T.translate('FILTERS.CRITICAL')}
          />
        </Box>
        <Divider />
        <QuickLookFilter
          backFilter={filters.quickLook?.[0]}
          aheadFilter={filters.quickLook?.[1]}
          onChange={onChangeFilter}
        />
        <Divider />
        <DateRangeFilter
          dateRange={filters.dateRange}
          onChange={onChangeFilter}
        />

        <FormField
          label={T.translate('FILTERS.RESOURCE')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
          style={{ paddingTop: 16 }}
        >
          <Dropdown
            style={{ width: '100%' }}
            isMultiple
            items={laborResources || []}
            selectedItems={resourceValues}
            onSelectedItemsChange={handleResourceSelectItemsChange}
          />
        </FormField>

        <FormField
          label={T.translate('FILTERS.STATUS')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
          style={{ paddingTop: 16 }}
        >
          <Dropdown
            style={{ width: '100%' }}
            isMultiple
            items={statusOptions}
            selectedItems={statusValues}
            onSelectedItemsChange={handleStatusSelectItemsChange}
          />
        </FormField>

        <ActivityCodeFilters
          activityCodesMap={activityCodesMap}
          filters={filters}
          onChangeFilter={onChangeFilter}
        />
      </Panel.Body>
    </Panel>
  );
}

export default FiltersPanel;
